import { translations } from "./locale";
import { splitMulti } from "./splitMulti";
import { Editor } from "grapesjs";
import { getTemplate, getTemplateOptions, DEFAULT_TEMPLATE_ID, TemplateProps } from "./templates";
import {
  DEFAULT_GRID_ITEM_TEMPLATE,
  DEFAULT_LIST_ITEM_TEMPLATE,
} from "./jobItemTemplates";
import { CustomCommands } from "@/shared/utils";

const loadFuseLib = () =>
  new Promise<void>((resolve) => {
    if (typeof window !== "undefined" && window.Fuse) {
      resolve();
      return;
    }

    const fuseScript = document.createElement("script");
    fuseScript.onload = () => resolve();
    fuseScript.src = "https://cdn.jsdelivr.net/npm/fuse.js@7.1.0";
    document.body.appendChild(fuseScript);
  });

if (typeof window !== "undefined") {
  // Expose for scripts that are stringified by GrapesJS
  (window as any).talentechLoadFuseLib = loadFuseLib;
}

const addScopedRules = (editor: Editor, componentId: string, rules: string[]) => {
  const scoped = rules.map((rule) => rule.replace(/__ID__/g, componentId)).join("\n");
  editor.CssComposer.addRules(scoped);
};

const normalizeCssSize = (value: string | undefined, fallback: string) => {
  if (!value) {
    return fallback;
  }
  const trimmed = value.toString().trim();
  if (trimmed === "") {
    return fallback;
  }
  if (/[a-z%]+$/i.test(trimmed)) {
    return trimmed;
  }
  const numeric = Number(trimmed);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  return `${numeric}px`;
};

type TalentechMessages = (typeof translations)[keyof typeof translations];
type TalentechFilterId = keyof TalentechMessages["filters"];

interface TalentechFeedPluginOptions {
  domain: string;
  source: string;
  locale: string;
}

enum CustomizableProps {
  TEMPLATE = "template",
  PAGE_SIZE = "pagination:pageSize",
  SHOW_SEARCH = "show:search",
  ITEM_TEMPLATE_GRID = "item-template:grid",
  ITEM_TEMPLATE_LIST = "item-template:list",
  VIEW_SWITCHER_BACKGROUND_COLOR = "view-switcher:backgroundColor",
  VIEW_SWITCHER_ICON_COLOR = "view-switcher:iconColor",
  VIEW_SWITCHER_ICON_SIZE = "view-switcher:iconSize",
  VIEW_SWITCHER_ACTIVE_BACKGROUND_COLOR = "view-switcher:activeBackgroundColor",
  VIEW_SWITCHER_ACTIVE_ICON_COLOR = "view-switcher:activeIconColor",
  CARD_BACKGROUND_COLOR = "card:backgroundColor",
  CARD_BORDER_RADIUS = "card:borderRadius",
  CAPTION_BACKGROUND_COLOR = "caption:backgroundColor",
  CAPTION_BORDER_RADIUS = "caption:borderRadius",
  HEADING_COLOR = "heading:color",
  PARAGRAPH_COLOR = "paragraph:color",
  BUTTON_BACKGROUND_COLOR = "button:backgroundColor",
  BUTTON_BORDER_RADIUS = "button:borderRadius",
  BUTTON_LABEL = "button:label",
  NO_DATA_TEXT = "no-data:text",
  TRANSLATIONS_FILTER_LOCATION_LABEL = "translations:filters:location:label",
  TRANSLATIONS_FILTER_LOCATION_ALL_OPTION = "translations:filters:location:allOption",
  TRANSLATIONS_FILTER_DEPARTMENT_LABEL = "translations:filters:department:label",
  TRANSLATIONS_FILTER_DEPARTMENT_ALL_OPTION = "translations:filters:department:allOption",
  TRANSLATIONS_FILTER_CATEGORY_LABEL = "translations:filters:category:label",
  TRANSLATIONS_FILTER_CATEGORY_ALL_OPTION = "translations:filters:category:allOption",
  TRANSLATIONS_FILTER_JOB_TYPE_LABEL = "translations:filters:job_type:label",
  TRANSLATIONS_FILTER_JOB_TYPE_ALL_OPTION = "translations:filters:job_type:allOption",
  TRANSLATIONS_FILTER_WORKPLACE_TYPE_LABEL = "translations:filters:workplace_type:label",
  TRANSLATIONS_FILTER_WORKPLACE_TYPE_ALL_OPTION = "translations:filters:workplace_type:allOption",
  TRANSLATIONS_JOBS_SINGULAR = "translations:jobs:singular",
  TRANSLATIONS_JOBS_PLURAL = "translations:jobs:plural",
  TRANSLATIONS_SEARCH_PLACEHOLDER = "translations:search:placeholder",

  FILTER_CATEGORY = "filter:category",
  FILTER_LOCATION = "filter:location",
  FILTER_DEPARTMENT = "filter:department",
  FILTER_JOB_TYPE = "filter:job_type",
  FILTER_WORKPLACE_TYPE = "filter:workplace_type",
  PREFILTER_CATEGORY = "prefilter:category",
  PREFILTER_LOCATION = "prefilter:location",
  PREFILTER_DEPARTMENT = "prefilter:department",
  PREFILTER_JOB_TYPE = "prefilter:job_type",
  PREFILTER_WORKPLACE_TYPE = "prefilter:workplace_type",
}

export interface TalentechFeedItem {
  ad_text: string;
  application_deadline: string;
  apply_url: string;
  department: string;
  category: string;
  job_id: string;
  location: string;
  project_id: string;
  title: string;
  salary_range: string;
  job_type: string;
  workplace_type: string;
  banner_url: string;
  logo_url: string;
  job_ad?: string;
}

export function talentechFeedPlugin(
  editor: Editor,
  options: TalentechFeedPluginOptions
) {
  const messages = translations[options.locale as keyof typeof translations] || translations.en;

  editor.TraitManager.addType("jobitem-template-button", {
    eventCapture: ["click button"],
    createInput() {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "btn btn-outline-primary btn-sm";
      button.textContent = "Edit GRID/LIST HTML";
      return button;
    },
    onEvent({ component, event }) {
      if (event.type !== "click") return;
      const ed = this.model.em.getEditor();
      ed.runCommand(CustomCommands.MODAL_JOBITEM_TEMPLATE_OPEN, {
        componentId: component.getId(),
      });
    },
  });

  editor.Blocks.add("jobs-feed-block", {
    label: "Jobs feed",
    category: "Extras",
    media: `<i class="fa-kit fa-cs-jobs fs-2"></i>`,
    content: { type: "talentech-feed" },
  });

  editor.Blocks.add("jobs-feed-more", {
    label: "Jobs feed counter",
    category: "Extras",
    media: `<i class="fa-sharp fa-solid fa-stopwatch-20 fs-2"></i>`,
    content: {
      content: `See available positions`,
      type: "talentech-feed-more",
    },
  });

  // editor.Blocks.add("jobs-live-search", {
  //   label: "Jobs live search",
  //   category: "Extras",
  //   media: `<i class="fa-solid fa-magnifying-glass fs-2"></i>`,
  //   content: { type: "talentech-feed-live-search" },
  // });

  editor.Components.addType("talentech-feed-more", {
    model: {
      defaults: {
        traits: [
          {
            name: "custom-text",
            label: "Custom text",
            type: "text",
            changeProp: true,
          },
        ],
        script: function (
          options: TalentechFeedPluginOptions & {
            messages: any;
            "custom-text"?: string;
          }
        ) {
          // @ts-ignore
          const wrapper = this as HTMLElement;
          if (options["custom-text"]) {
            wrapper.innerHTML = options["custom-text"];
            return;
          }

          let jobsMessages = options.messages.jobs as {
            singular: string;
            plural: string;
          };

          const resolveJobMessages = () => jobsMessages;

          const getPositionsText = (
            message: { singular: string; plural: string },
            count: number
          ) => {
            const template = count === 1 ? message.singular : message.plural;
            return template.replace("{count}", count.toString());
          };
          let cachedCount: number | null = null;

          const renderCountText = (count: number) => {
            const jobMessages = resolveJobMessages();
            wrapper.innerHTML = getPositionsText(jobMessages, count);
          };

          const getJobCount = () => {
            fetch(`${options.source}`).then((res) => {
              res.json().then((res: TalentechFeedItem[]) => {
                const count = res.length;
                cachedCount = count;
                renderCountText(count);
              });
            });
          };

          const handleMessagesUpdated = (event: Event) => {
            if (options["custom-text"] || cachedCount === null) {
              return;
            }
            const customEvent = event as CustomEvent<TalentechMessages>;
            if (customEvent.detail?.jobs) {
              jobsMessages = customEvent.detail.jobs;
            }
            wrapper.innerHTML = getPositionsText(resolveJobMessages(), cachedCount);
          };

          window.addEventListener(
            "talentech:messagesUpdated",
            handleMessagesUpdated as EventListener
          );

          getJobCount();
        },
        ...options,
        messages,
        "script-props": ["source", "locale", "messages", "custom-text"],
      },
    },
  });

  const SCRIPT_LOGIC_PROPS = [
    CustomizableProps.TEMPLATE,
    CustomizableProps.PAGE_SIZE,
    CustomizableProps.SHOW_SEARCH,
    CustomizableProps.ITEM_TEMPLATE_GRID,
    CustomizableProps.ITEM_TEMPLATE_LIST,
    CustomizableProps.NO_DATA_TEXT,
    CustomizableProps.BUTTON_LABEL,
    CustomizableProps.FILTER_CATEGORY,
    CustomizableProps.FILTER_LOCATION,
    CustomizableProps.FILTER_DEPARTMENT,
    CustomizableProps.FILTER_JOB_TYPE,
    CustomizableProps.FILTER_WORKPLACE_TYPE,
    CustomizableProps.PREFILTER_CATEGORY,
    CustomizableProps.PREFILTER_LOCATION,
    CustomizableProps.PREFILTER_DEPARTMENT,
    CustomizableProps.PREFILTER_JOB_TYPE,
    CustomizableProps.PREFILTER_WORKPLACE_TYPE,
    CustomizableProps.TRANSLATIONS_FILTER_LOCATION_LABEL,
    CustomizableProps.TRANSLATIONS_FILTER_LOCATION_ALL_OPTION,
    CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_LABEL,
    CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_ALL_OPTION,
    CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_LABEL,
    CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_ALL_OPTION,
    CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_LABEL,
    CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_ALL_OPTION,
    CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_LABEL,
    CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_ALL_OPTION,
    CustomizableProps.TRANSLATIONS_JOBS_SINGULAR,
    CustomizableProps.TRANSLATIONS_JOBS_PLURAL,
    CustomizableProps.TRANSLATIONS_SEARCH_PLACEHOLDER,
  ];

  editor.Components.addType("talentech-feed", {
    model: {
      defaults: {
        script,
        ...options,
        messages,
        style: {},
        template: DEFAULT_TEMPLATE_ID,
        [CustomizableProps.ITEM_TEMPLATE_GRID]: DEFAULT_GRID_ITEM_TEMPLATE,
        [CustomizableProps.ITEM_TEMPLATE_LIST]: DEFAULT_LIST_ITEM_TEMPLATE,
        traits: [
          {
            name: CustomizableProps.TEMPLATE,
            type: "select",
            label: "Layout Template",
            options: getTemplateOptions(),
            changeProp: true,
            default: DEFAULT_TEMPLATE_ID,
            category: "Layout",
          },
          {
            type: "jobitem-template-button",
            label: "Item templates",
            category: "Layout",
          },
          {
            name: CustomizableProps.VIEW_SWITCHER_BACKGROUND_COLOR,
            type: "color",
            label: "View switcher background",
            changeProp: true,
            default: "#ffffff",
            category: "View switcher",
          },
          {
            name: CustomizableProps.VIEW_SWITCHER_ACTIVE_BACKGROUND_COLOR,
            type: "color",
            label: "View switcher active background",
            changeProp: true,
            default: "#1265ff",
            category: "View switcher",
          },
          {
            name: CustomizableProps.VIEW_SWITCHER_ICON_COLOR,
            type: "color",
            label: "View switcher icon color",
            changeProp: true,
            default: "#495057",
            category: "View switcher",
          },
          {
            name: CustomizableProps.VIEW_SWITCHER_ACTIVE_ICON_COLOR,
            type: "color",
            label: "View switcher active icon color",
            changeProp: true,
            default: "#ffffff",
            category: "View switcher",
          },
          {
            name: CustomizableProps.VIEW_SWITCHER_ICON_SIZE,
            type: "number",
            label: "View switcher icon size",
            min: 12,
            max: 48,
            changeProp: true,
            default: 16,
            category: "View switcher",
          },
          {
            name: CustomizableProps.PAGE_SIZE,
            type: "number",
            label: "Items per page",
            min: 3,
            max: 50,
            default: 9,
            changeProp: true,
            category: "Layout",
          },
          {
            name: CustomizableProps.SHOW_SEARCH,
            type: "checkbox",
            label: "Show search box",
            changeProp: true,
            category: "Layout",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.CARD_BACKGROUND_COLOR,
            type: "color",
            label: "Background color",
            changeProp: true,
            default: "#FFF",
            category: "Card",
          },
          {
            name: CustomizableProps.CARD_BORDER_RADIUS,
            type: "checkbox",
            valueTrue: "10px",
            valueFalse: "0px",
            label: "Rounded corners",
            changeProp: true,
            category: "Card",
          },
          {
            name: CustomizableProps.CAPTION_BACKGROUND_COLOR,
            type: "color",
            label: "Background color",
            changeProp: true,
            default: "#ffffff",
            category: "Caption",
          },
          {
            name: CustomizableProps.CAPTION_BORDER_RADIUS,
            type: "checkbox",
            valueTrue: "4px",
            valueFalse: "0px",
            label: "Rounded corners",
            changeProp: true,
            category: "Caption",
          },
          {
            name: CustomizableProps.HEADING_COLOR,
            type: "color",
            label: "Heading color",
            changeProp: true,
            default: "#000",
            category: "Heading",
          },
          {
            name: CustomizableProps.PARAGRAPH_COLOR,
            type: "color",
            label: "Paragraph color",
            changeProp: true,
            default: "#000",
            category: "Paragraph",
          },
          {
            name: CustomizableProps.BUTTON_BACKGROUND_COLOR,
            type: "color",
            label: "Button background color",
            changeProp: true,
            default: "#1265ff",
            category: "Button",
          },
          {
            name: CustomizableProps.BUTTON_BORDER_RADIUS,
            type: "checkbox",
            valueTrue: "20px",
            valueFalse: "4px",
            label: "Rounded corners",
            changeProp: true,
            category: "Button",
          },
          {
            name: CustomizableProps.BUTTON_LABEL,
            type: "text",
            label: "label",
            default: "See details",
            changeProp: true,
            category: "Button",
          },
          {
            name: CustomizableProps.NO_DATA_TEXT,
            label: "No data text",
            type: "text",
            default: "No job ads found",
            changeProp: true,
            category: "Others",
          },
          {
            name: CustomizableProps.FILTER_CATEGORY,
            label: "Category",
            type: "checkbox",
            changeProp: true,
            category: "Filter",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.FILTER_LOCATION,
            label: "Location",
            type: "checkbox",
            changeProp: true,
            category: "Filter",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.FILTER_DEPARTMENT,
            label: "Department",
            type: "checkbox",
            changeProp: true,
            category: "Filter",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.FILTER_JOB_TYPE,
            label: "Job Type",
            type: "checkbox",
            changeProp: true,
            category: "Filter",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.FILTER_WORKPLACE_TYPE,
            label: "Workplace Type",
            type: "checkbox",
            changeProp: true,
            category: "Filter",
            valueTrue: "true",
            valueFalse: "false",
            default: "true",
          },
          {
            name: CustomizableProps.PREFILTER_LOCATION,
            label: "Prefilter Location",
            type: "select",
            options: [{ id: "", name: "All" }],
            changeProp: true,
            category: "Prefilter",
          },
          {
            name: CustomizableProps.PREFILTER_DEPARTMENT,
            label: "Prefilter Department",
            type: "select",
            options: [{ id: "", name: "All" }],
            changeProp: true,
            category: "Prefilter",
          },
          {
            name: CustomizableProps.PREFILTER_CATEGORY,
            label: "Prefilter Category",
            type: "select",
            options: [{ id: "", name: "All" }],
            changeProp: true,
            category: "Prefilter",
          },
          {
            name: CustomizableProps.PREFILTER_JOB_TYPE,
            label: "Prefilter Job Type",
            type: "select",
            options: [{ id: "", name: "All" }],
            changeProp: true,
            category: "Prefilter",
          },
          {
            name: CustomizableProps.PREFILTER_WORKPLACE_TYPE,
            label: "Prefilter Workplace Type",
            type: "select",
            options: [{ id: "", name: "All" }],
            changeProp: true,
            category: "Prefilter",
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_LOCATION_LABEL,
            type: "text",
            label: "Location label override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.location.label,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_LOCATION_ALL_OPTION,
            type: "text",
            label: "Location all option override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.location.allOption,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_LABEL,
            type: "text",
            label: "Department label override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.department.label,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_ALL_OPTION,
            type: "text",
            label: "Department all option override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.department.allOption,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_LABEL,
            type: "text",
            label: "Category label override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.category.label,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_ALL_OPTION,
            type: "text",
            label: "Category all option override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.category.allOption,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_LABEL,
            type: "text",
            label: "Job Type label override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.job_type.label,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_ALL_OPTION,
            type: "text",
            label: "Job Type all option override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.job_type.allOption,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_LABEL,
            type: "text",
            label: "Workplace Type label override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.workplace_type.label,
          },
          {
            name: CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_ALL_OPTION,
            type: "text",
            label: "Workplace Type all option override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.filters.workplace_type.allOption,
          },
          {
            name: CustomizableProps.TRANSLATIONS_JOBS_SINGULAR,
            type: "text",
            label: "Jobs singular override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.jobs.singular,
          },
          {
            name: CustomizableProps.TRANSLATIONS_JOBS_PLURAL,
            type: "text",
            label: "Jobs plural override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.jobs.plural,
          },
          {
            name: CustomizableProps.TRANSLATIONS_SEARCH_PLACEHOLDER,
            type: "text",
            label: "Search placeholder override",
            changeProp: true,
            category: "Translations",
            placeholder: messages.search?.placeholder || "Search jobs by title...",
          },
        ],
        "script-props": ["source", "domain", "messages", ...SCRIPT_LOGIC_PROPS],
      },
      init() {
        const componentId = this.getId();
        addScopedRules(editor, componentId, [
          `#__ID__ #search-container{
          width: 100%;
          margin-bottom: 1rem;
          }`,
          `#__ID__ #search-container .search-wrapper{
          position: relative;
          display: flex;
          align-items: center;
          }`,
          `#__ID__ #search-container input{
          width: 100%;
          padding: .75rem 2.5rem .75rem 1rem;
          font-size: 1rem;
          line-height: 1.5;
          color: #495057;
          background-color: #fff;
          border: 1px solid #ced4da;
          border-radius: .25rem;
          transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
          }`,
          `#__ID__ #search-container input:focus{
          outline: 2px solid #1265ff;
          outline-offset: 2px;
          border-color: #1265ff;
          }`,
          `#__ID__ #search-container .clear-search{
          position: absolute;
          right: 0.5rem;
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 0.25rem 0.5rem;
          font-size: 1.25rem;
          line-height: 1;
          opacity: 0;
          pointer-events: none;
          transition: opacity .15s ease-in-out;
          }`,
          `#__ID__ #search-container .clear-search.visible{
          opacity: 1;
          pointer-events: auto;
          }`,
          `#__ID__ #search-container .clear-search:hover{
          color: #495057;
          }`,
          `#__ID__ #search-container .clear-search:focus{
          outline: 2px solid #1265ff;
          outline-offset: 2px;
          border-radius: .25rem;
          }`,
          `#__ID__ #filters-view-container{
          width: 100%;
          margin-bottom: 1rem;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 1rem;
          flex-wrap: wrap;
          }`,
          `#__ID__ #filters-container{
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          flex: 1;
          min-width: 0;
          }`,
          `#__ID__ #view-switcher{
          display: flex;
          gap: 0.5rem;
          flex-shrink: 0;
          }`,
          `#__ID__ #view-switcher button{
          padding: 0.5rem;
          border: 1px solid color-mix(in srgb, var(--view-switcher-bg, #fff) 90%, black);
          background-color: var(--view-switcher-bg, #fff);
          color: #495057;
          cursor: pointer;
          border-radius: .25rem;
          transition: all .15s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: center;
          }`,
          `#__ID__ #view-switcher button svg{
          width: var(--view-switcher-icon-size, 24px);
          height: var(--view-switcher-icon-size, 24px);
          }`,
          `#__ID__ #view-switcher button svg, #__ID__ #view-switcher button svg path{
          fill: var(--view-switcher-icon-color, #495057);
          }`,
          `#__ID__ #view-switcher button.active{
          background-color: var(--view-switcher-active-bg, #1265ff);
          color: #fff;
          border-color: color-mix(in srgb, var(--view-switcher-active-bg, #1265ff) 90%, black);
          }`,
          `#__ID__ #view-switcher button.active svg, #__ID__ #view-switcher button.active svg path{
          fill: var(--view-switcher-active-icon-color, #ffffff);
          }`,
          `#__ID__ #view-switcher button:hover{
          background-color: color-mix(in srgb, var(--view-switcher-bg, #fff) 80%, black);
          }`,
          `#__ID__ #view-switcher button.active:hover{
          background-color: color-mix(in srgb, var(--view-switcher-active-bg, #1265ff) 80%, black);
          }`,
          `#__ID__ #view-switcher button:focus{
          outline: 2px solid #1265ff;
          outline-offset: 2px;
          }`,
          `#__ID__ #view-switcher button:focus:not(:focus-visible){
          outline: none;
          }`,
          `#__ID__ #view-switcher button:focus-visible{
          outline: 2px solid #1265ff;
          outline-offset: 2px;
          }`,
          `#__ID__ .sr-only{
          position: absolute;
          width: 1px;
          height: 1px;
          padding: 0;
          margin: -1px;
          overflow: hidden;
          clip: rect(0, 0, 0, 0);
          white-space: nowrap;
          border: 0;
          }`,
          `@media (max-width: 576px) {
          #__ID__ #filters-view-container{
          flex-direction: column;
          align-items: stretch;
        }}`,
          `@media (max-width: 576px) {
          #__ID__ #filters-container{
          flex-direction: column;
        }}`,
          `@media (max-width: 576px) {
          #__ID__ #view-switcher{
          width: 100%;
          justify-content: center;
        }}`,
          `#__ID__ #filters-container .filter-form-control{
          display: flex;
          flex-direction: column;
          min-width: 150px;
          }`,
          `#__ID__ #filters-container label{
          margin-bottom: .5rem;
          }`,
          `#__ID__ #filters-container select{
          width: 100%;
    height: calc(1.5em + .75rem + 2px);
    padding: .375rem .75rem;
    font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #ced4da;
    border-radius: .25rem;
    transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
          }`,
          `#__ID__ #pagination-container .page-link{
          border: 1px solid color-mix(in srgb, var(--view-switcher-bg, #fff) 90%, black);
          background-color: var(--view-switcher-bg, #fff);
          color: var(--view-switcher-icon-color, #495057);
          }`,
          `#__ID__ #pagination-container .page-link:hover{
          background-color: color-mix(in srgb, var(--view-switcher-bg, #fff) 80%, black);
          }`,
          `#__ID__ #pagination-container .page-item.active .page-link{
          background-color: var(--view-switcher-active-bg, #1265ff);
          color: var(--view-switcher-active-icon-color, #ffffff);
          border-color: color-mix(in srgb, var(--view-switcher-active-bg, #1265ff) 90%, black);
          }`,
          `#__ID__ #pagination-container .page-item.active .page-link:hover{
          background-color: color-mix(in srgb, var(--view-switcher-active-bg, #1265ff) 80%, black);
          }`,
          `#__ID__ #pagination-container .page-item.disabled .page-link{
          opacity: 0.5;
          cursor: default;
          pointer-events: none;
          }`,
          `#__ID__ #pagination-container .page-link:focus{
          outline: 2px solid color-mix(in srgb, var(--view-switcher-bg, #1265ff) 80%, black);
          outline-offset: 2px;
          }`,
          `#__ID__ #pagination-container .page-link:focus:not(:focus-visible){
          outline: none;
          }`,
          `#__ID__ #pagination-container .page-link:focus-visible{
          outline: 2px solid color-mix(in srgb, var(--view-switcher-bg, #1265ff) 80%, black);
          outline-offset: 2px;
          }`,
          `#__ID__ #pagination-container .pagination-info{
          font-size: 0.875rem;
          }`,
        ]);

        // Inject both grid and list layout CSS so users can switch at runtime
        const gridTemplate = getTemplate("grid");
        const listTemplate = getTemplate("list");

        editor.CssComposer.addRules(gridTemplate.generateLayoutCSS(componentId));
        editor.CssComposer.addRules(listTemplate.generateLayoutCSS(componentId));

        // Initialize with template-specific styles
        this.applyTemplateStyles();
        this.saveStyles();

        const source = this.get("source");
        if (source) {
          fetch(source)
            .then((res) => res.json())
            .then((jobs: TalentechFeedItem[]) => {
              const locationTrait = this.getTrait(
                CustomizableProps.PREFILTER_LOCATION
              );
              if (locationTrait) {
                const locations = [
                  ...new Set(jobs.flatMap((j) => splitMulti(j.location))),
                ].sort();
                locationTrait.set("options", [
                  { id: "", name: "All" },
                  ...locations.map((l) => ({ id: l, name: l })),
                ]);
              }

              const departmentTrait = this.getTrait(
                CustomizableProps.PREFILTER_DEPARTMENT
              );
              if (departmentTrait) {
                const departments = [
                  ...new Set(jobs.flatMap((j) => splitMulti(j.department))),
                ].sort();
                departmentTrait.set("options", [
                  { id: "", name: "All" },
                  ...departments.map((d) => ({ id: d, name: d })),
                ]);
              }

              const categoryTrait = this.getTrait(
                CustomizableProps.PREFILTER_CATEGORY
              );
              if (categoryTrait) {
                const categories = [
                  ...new Set(jobs.flatMap((j) => splitMulti(j.category))),
                ].sort();
                categoryTrait.set("options", [
                  { id: "", name: "All" },
                  ...categories.map((c) => ({ id: c, name: c })),
                ]);
              }

              const jobTypeTrait = this.getTrait(
                CustomizableProps.PREFILTER_JOB_TYPE
              );
              if (jobTypeTrait) {
                const jobTypes = [
                  ...new Set(jobs.flatMap((j) => splitMulti(j.job_type))),
                ].sort();
                jobTypeTrait.set("options", [
                  { id: "", name: "All" },
                  ...jobTypes.map((jt) => ({ id: jt, name: jt })),
                ]);
              }

              const workplaceTypeTrait = this.getTrait(
                CustomizableProps.PREFILTER_WORKPLACE_TYPE
              );
              if (workplaceTypeTrait) {
                const workplaceTypes = [
                  ...new Set(jobs.flatMap((j) => splitMulti(j.workplace_type))),
                ].sort();
                workplaceTypeTrait.set("options", [
                  { id: "", name: "All" },
                  ...workplaceTypes.map((wt) => ({ id: wt, name: wt })),
                ]);
              }
            });
        }
      },
      applyTemplateStyles() {
        // Set data-template attribute based on selected template
        const selectedTemplate = this.get(CustomizableProps.TEMPLATE) || DEFAULT_TEMPLATE_ID;
        this.view?.el.setAttribute('data-template', selectedTemplate);
      },
      saveStyles() {
        const componentId = this.getId();

        // Get all style props
        const props: TemplateProps = {
          "card:backgroundColor": this.getTrait(CustomizableProps.CARD_BACKGROUND_COLOR)?.getValue() || "#FFF",
          "card:borderRadius": this.getTrait(CustomizableProps.CARD_BORDER_RADIUS)?.getValue() || "0px",
          "caption:backgroundColor": this.getTrait(CustomizableProps.CAPTION_BACKGROUND_COLOR)?.getValue() || "#1265ff",
          "caption:borderRadius": this.getTrait(CustomizableProps.CAPTION_BORDER_RADIUS)?.getValue() || "0px",
          "heading:color": this.getTrait(CustomizableProps.HEADING_COLOR)?.getValue() || "#000",
          "paragraph:color": this.getTrait(CustomizableProps.PARAGRAPH_COLOR)?.getValue() || "#000",
          "button:backgroundColor": this.getTrait(CustomizableProps.BUTTON_BACKGROUND_COLOR)?.getValue() || "#1265ff",
          "button:borderRadius": this.getTrait(CustomizableProps.BUTTON_BORDER_RADIUS)?.getValue() || "4px",
          "button:label": this.getTrait(CustomizableProps.BUTTON_LABEL)?.getValue() || "See details",
          "no-data:text": this.getTrait(CustomizableProps.NO_DATA_TEXT)?.getValue() || "No job ads found",
          domain: this.get("domain") || "",
        };
        const gridTemplate = getTemplate("grid");
        const listTemplate = getTemplate("list");
        // Apply template-specific styling CSS
        editor.CssComposer.addRules(gridTemplate.generateStylingCSS(componentId, props));
        editor.CssComposer.addRules(listTemplate.generateStylingCSS(componentId, props));

        const viewSwitcherIconSize = normalizeCssSize(
          this.getTrait(CustomizableProps.VIEW_SWITCHER_ICON_SIZE)?.getValue(),
          "24px"
        );

        addScopedRules(editor, componentId, [
          `#__ID__{
            --view-switcher-bg: ${
              this.getTrait(CustomizableProps.VIEW_SWITCHER_BACKGROUND_COLOR)?.getValue() ||
              "#ffffff"
            };
            --view-switcher-active-bg: ${
              this.getTrait(CustomizableProps.VIEW_SWITCHER_ACTIVE_BACKGROUND_COLOR)?.getValue() ||
              "#1265ff"
            };
            --view-switcher-icon-color: ${
              this.getTrait(CustomizableProps.VIEW_SWITCHER_ICON_COLOR)?.getValue() ||
              "#495057"
            };
            --view-switcher-active-icon-color: ${
              this.getTrait(CustomizableProps.VIEW_SWITCHER_ACTIVE_ICON_COLOR)?.getValue() ||
              "#ffffff"
            };
            --view-switcher-icon-size: ${viewSwitcherIconSize};
          }`,
        ]);
      },
      updated(prop, value) {
        if (!Object.values(CustomizableProps).includes(prop as any)) {
          return;
        }

        if (SCRIPT_LOGIC_PROPS.includes(prop as CustomizableProps)) {
          this.set(prop, value);

          // If template changed, reapply both layout and styling
          if (prop === CustomizableProps.TEMPLATE) {
            this.applyTemplateStyles();
          }
        }
          this.saveStyles();
      },
    },
  });

  // Live Search Component with Dropdown
  editor.Components.addType("talentech-feed-live-search", {
    model: {
      defaults: {
        traits: [
          {
            name: "max-results",
            label: "Max results to show",
            type: "number",
            min: 3,
            max: 10,
            default: 5,
            changeProp: true,
          },
          {
            name: "threshold",
            label: "Threshold for 'See All'",
            type: "number",
            min: 1,
            max: 20,
            default: 3,
            changeProp: true,
          },
          {
            name: "placeholder",
            label: "Placeholder text",
            type: "text",
            default: "Search jobs...",
            changeProp: true,
          },
        ],
        script: liveSearchScript,
        ...options,
        "script-props": ["source", "domain", "max-results", "threshold", "placeholder"],
      },
    },
  });
}

declare global {
  interface Window {
    Fuse: any;
  talentechLoadFuseLib?: () => Promise<void>;
  }
}

interface TalentechFeedState {
  loaded: boolean;
  data: TalentechFeedItem[];
  adsWrapper: HTMLElement | null;
  filtersContainer: HTMLElement | null;
  paginationContainer: HTMLElement | null;
  liveRegion: HTMLElement | null;
  messages: TalentechMessages;
  filters: Array<{
    id: TalentechFilterId;
    value: string;
  }>;
  currentPage: number;
  currentView: string;
  pageSize: number;
  fuse: any;
  searchTerm: string;
  componentId: string;
}

const liveSearchScript = function (
  props: TalentechFeedPluginOptions & {
    "max-results": string;
    threshold: string;
    placeholder: string;
  }
) {
  // @ts-ignore
  const wrapper = this as HTMLElement;
  wrapper.style.position = "relative";
  wrapper.style.width = "100%";
  wrapper.style.maxWidth = "600px";

  const ensureFuse = () => {
    if (window.talentechLoadFuseLib) {
      return window.talentechLoadFuseLib();
    }
    return new Promise<void>((resolve) => {
      if (window.Fuse) {
        resolve();
        return;
      }
      const fuseScript = document.createElement("script");
      fuseScript.onload = () => resolve();
      fuseScript.src = "https://cdn.jsdelivr.net/npm/fuse.js@7.1.0";
      document.body.appendChild(fuseScript);
    });
  };

  // Create search input
  const searchInput = document.createElement("input");
  searchInput.type = "text";
  searchInput.placeholder = props.placeholder || "Search jobs...";
  searchInput.setAttribute("aria-label", "Live search for jobs");
  searchInput.setAttribute("role", "combobox");
  searchInput.setAttribute("aria-expanded", "false");
  searchInput.setAttribute("aria-autocomplete", "list");
  searchInput.setAttribute("aria-controls", "live-search-results");
  searchInput.style.width = "100%";
  searchInput.style.padding = ".75rem 1rem";
  searchInput.style.fontSize = "1rem";
  searchInput.style.border = "1px solid #ced4da";
  searchInput.style.borderRadius = ".25rem";
  searchInput.style.outline = "none";
  wrapper.appendChild(searchInput);

  // Create dropdown results container
  const resultsContainer = document.createElement("ul");
  resultsContainer.id = "live-search-results";
  resultsContainer.setAttribute("role", "listbox");
  resultsContainer.style.position = "absolute";
  resultsContainer.style.top = "100%";
  resultsContainer.style.left = "0";
  resultsContainer.style.right = "0";
  resultsContainer.style.backgroundColor = "#fff";
  resultsContainer.style.border = "1px solid #ced4da";
  resultsContainer.style.borderTop = "none";
  resultsContainer.style.borderRadius = "0 0 .25rem .25rem";
  resultsContainer.style.maxHeight = "400px";
  resultsContainer.style.overflowY = "auto";
  resultsContainer.style.listStyle = "none";
  resultsContainer.style.margin = "0";
  resultsContainer.style.padding = "0";
  resultsContainer.style.display = "none";
  resultsContainer.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
  resultsContainer.style.zIndex = "1000";
  wrapper.appendChild(resultsContainer);

  // Load Fuse.js
  ensureFuse().then(() => {
    fetch(props.source)
      .then((res) => res.json())
      .then((jobs: TalentechFeedItem[]) => {

        // Initialize Fuse.js
        const fuse = new window.Fuse(jobs, {
          keys: ['title', 'location', 'department'],
          threshold: 0.3,
          includeScore: true,
          minMatchCharLength: 2,
        });

        let selectedIndex = -1;
        let currentQuery = "";

        const showResults = (results: any[], query: string) => {
          resultsContainer.innerHTML = "";
          currentQuery = query;

          if (results.length === 0) {
            resultsContainer.style.display = "none";
            searchInput.setAttribute("aria-expanded", "false");
            return;
          }

          const maxResults = parseInt(props["max-results"] || "5", 10);
          const threshold = parseInt(props["threshold"] || "3", 10);
          const displayResults = results.slice(0, maxResults);

          displayResults.forEach((result, index) => {
            const job = result.item;
            const li = document.createElement("li");
            li.setAttribute("role", "option");
            li.setAttribute("aria-selected", index === selectedIndex ? "true" : "false");
            li.style.padding = ".75rem 1rem";
            li.style.cursor = "pointer";
            li.style.borderBottom = "1px solid #e9ecef";
            li.style.transition = "background-color 0.15s";

            const title = document.createElement("div");
            title.textContent = job.title;
            title.style.fontWeight = "600";
            title.style.marginBottom = ".25rem";
            li.appendChild(title);

            const meta = document.createElement("div");
            meta.style.fontSize = ".875rem";
            meta.style.color = "#6c757d";
            const metaParts = [job.location, job.department].filter(Boolean);
            meta.textContent = metaParts.join(" • ");
            li.appendChild(meta);

            li.addEventListener("mouseenter", () => {
              li.style.backgroundColor = "#f8f9fa";
            });

            li.addEventListener("mouseleave", () => {
              li.style.backgroundColor = "";
            });

            li.addEventListener("click", () => {
              // NOTE: keep in sync with jobLink.ts
              window.location.href =
                job.job_ad && /^https?:\/\//i.test(job.job_ad) ? job.job_ad : `${props.domain}/job/${job.job_id}`;
            });

            resultsContainer.appendChild(li);
          });

          // Add "SEE ALL" option if results exceed threshold
          if (results.length > threshold) {
            const seeAllLi = document.createElement("li");
            seeAllLi.setAttribute("role", "option");
            seeAllLi.style.padding = ".75rem 1rem";
            seeAllLi.style.cursor = "pointer";
            seeAllLi.style.backgroundColor = "#f8f9fa";
            seeAllLi.style.textAlign = "center";
            seeAllLi.style.fontWeight = "600";
            seeAllLi.style.color = "#1265ff";
            seeAllLi.style.transition = "background-color 0.15s";
            seeAllLi.style.borderTop = "2px solid #dee2e6";
            seeAllLi.style.display = "block";
            seeAllLi.textContent = `SEE ALL (${results.length} results)`;

            seeAllLi.addEventListener("mouseenter", () => {
              seeAllLi.style.backgroundColor = "#e9ecef";
            });

            seeAllLi.addEventListener("mouseleave", () => {
              seeAllLi.style.backgroundColor = "#f8f9fa";
            });

            seeAllLi.addEventListener("click", () => {
              // Find the main job feed component and apply search
              const jobFeed = document.querySelector('[data-gjs-type="talentech-feed"]');
              if (jobFeed) {
                const mainSearchInput = jobFeed.querySelector('#job-search-input') as HTMLInputElement;
                if (mainSearchInput) {
                  // Set the search value
                  mainSearchInput.value = currentQuery;
                  mainSearchInput.dispatchEvent(new Event('input', { bubbles: true }));

                  // Scroll to the job feed
                  jobFeed.scrollIntoView({ behavior: 'smooth', block: 'start' });

                  // Close the live search dropdown
                  resultsContainer.style.display = "none";
                  searchInput.setAttribute("aria-expanded", "false");

                  // Focus the main search input
                  setTimeout(() => {
                    mainSearchInput.focus();
                  }, 500);
                }
              }
            });

            resultsContainer.appendChild(seeAllLi);
          }

          resultsContainer.style.display = "block";
          searchInput.setAttribute("aria-expanded", "true");
        };

        // Search input handler
        searchInput.addEventListener("input", (e: Event) => {
          const target = e.target as HTMLInputElement;
          const query = target.value.trim();

          if (query.length < 2) {
            resultsContainer.style.display = "none";
            searchInput.setAttribute("aria-expanded", "false");
            return;
          }

          const results = fuse.search(query);
          showResults(results, query);
          selectedIndex = -1;
        });

        // Keyboard navigation
        searchInput.addEventListener("keydown", (e: KeyboardEvent) => {
          const items = resultsContainer.querySelectorAll("li");

          if (e.key === "ArrowDown") {
            e.preventDefault();
            selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
            items.forEach((item, idx) => {
              if (idx === selectedIndex) {
                (item as HTMLElement).style.backgroundColor = "#e9ecef";
                item.setAttribute("aria-selected", "true");
              } else {
                (item as HTMLElement).style.backgroundColor = "";
                item.setAttribute("aria-selected", "false");
              }
            });
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            selectedIndex = Math.max(selectedIndex - 1, -1);
            items.forEach((item, idx) => {
              if (idx === selectedIndex) {
                (item as HTMLElement).style.backgroundColor = "#e9ecef";
                item.setAttribute("aria-selected", "true");
              } else {
                (item as HTMLElement).style.backgroundColor = "";
                item.setAttribute("aria-selected", "false");
              }
            });
          } else if (e.key === "Enter" && selectedIndex >= 0) {
            e.preventDefault();
            (items[selectedIndex] as HTMLElement).click();
          } else if (e.key === "Escape") {
            resultsContainer.style.display = "none";
            searchInput.setAttribute("aria-expanded", "false");
            selectedIndex = -1;
          }
        });

        // Close dropdown when clicking outside
        document.addEventListener("click", (e: MouseEvent) => {
          if (!wrapper.contains(e.target as Node)) {
            resultsContainer.style.display = "none";
            searchInput.setAttribute("aria-expanded", "false");
            selectedIndex = -1;
          }
        });

        // Focus styles
        searchInput.addEventListener("focus", () => {
          searchInput.style.borderColor = "#1265ff";
          searchInput.style.boxShadow = "0 0 0 2px rgba(33, 111, 255, 0.25)";
        });

        searchInput.addEventListener("blur", () => {
          searchInput.style.borderColor = "#ced4da";
          searchInput.style.boxShadow = "none";
        });
      });
  });
};

const script = function (
  props: Record<CustomizableProps, string> &
    TalentechFeedPluginOptions & { messages: any }
) {
  // @ts-ignore
  const wrapper = this as HTMLElement;

// NOTE: keep in sync with splitMulti.ts
  const splitMulti = (raw: string | undefined | null): string[] =>
    (raw ?? "")
      .split(/[;,]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const cloneMessages = (input: TalentechMessages | undefined): TalentechMessages =>
    JSON.parse(JSON.stringify(input || {})) as TalentechMessages;

  const getState = (): TalentechFeedState => {
    const existing = (wrapper as any).__talentechState as
      | TalentechFeedState
      | undefined;
    if (existing) {
      return existing;
    }

    const initialState: TalentechFeedState = {
      loaded: false,
      data: [],
      adsWrapper: null,
      filtersContainer: null,
      paginationContainer: null,
      liveRegion: null,
      messages: cloneMessages(props.messages),
      filters: [],
      currentPage: 1,
      currentView: props["template"] || "grid",
      pageSize: parseInt(props[CustomizableProps.PAGE_SIZE] || "9", 10),
      fuse: null,
      searchTerm: "",
      componentId: wrapper.id || "",
    };

    (wrapper as any).__talentechState = initialState;
    return initialState;
  };

  const state = getState();

  const messages: TalentechMessages = state.messages;

  const setNestedValue = (target: any, path: string[], value: string) => {
    let current = target;
    for (let i = 0; i < path.length - 1; i++) {
      const key = path[i];
      if (!current[key] || typeof current[key] !== "object") {
        current[key] = {};
      }
      current = current[key];
    }
    current[path[path.length - 1]] = value;
  };

  const translationOverrides: Array<{ key: CustomizableProps; path: string[] }> = [
    { key: CustomizableProps.TRANSLATIONS_FILTER_LOCATION_LABEL, path: ["filters", "location", "label"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_LOCATION_ALL_OPTION, path: ["filters", "location", "allOption"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_LABEL, path: ["filters", "department", "label"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_DEPARTMENT_ALL_OPTION, path: ["filters", "department", "allOption"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_LABEL, path: ["filters", "category", "label"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_CATEGORY_ALL_OPTION, path: ["filters", "category", "allOption"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_LABEL, path: ["filters", "job_type", "label"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_JOB_TYPE_ALL_OPTION, path: ["filters", "job_type", "allOption"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_LABEL, path: ["filters", "workplace_type", "label"] },
    { key: CustomizableProps.TRANSLATIONS_FILTER_WORKPLACE_TYPE_ALL_OPTION, path: ["filters", "workplace_type", "allOption"] },
    { key: CustomizableProps.TRANSLATIONS_JOBS_SINGULAR, path: ["jobs", "singular"] },
    { key: CustomizableProps.TRANSLATIONS_JOBS_PLURAL, path: ["jobs", "plural"] },
    { key: CustomizableProps.TRANSLATIONS_SEARCH_PLACEHOLDER, path: ["search", "placeholder"] },
  ];

  translationOverrides.forEach(({ key, path }) => {
    const overrideValue = props[key];
    if (typeof overrideValue === "string" && overrideValue.trim() !== "") {
      setNestedValue(messages, path, overrideValue);
    }
  });

  const broadcastMessagesUpdated = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("talentech:messagesUpdated", {
          detail: messages,
        })
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn("Failed to broadcast Talentech messages update", error);
    }
  };

  // Create search container with accessibility (conditionally)
  const showSearch = props[CustomizableProps.SHOW_SEARCH] === "true";
  let searchContainer: HTMLElement | null = null;
  let searchWrapper: HTMLElement | null = null;
  let searchInput: HTMLInputElement | null = null;
  let clearSearchBtn: HTMLButtonElement | null = null;

  const searchMessages = messages.search || {
    placeholder: "Search jobs by title...",
  };

  if (showSearch) {
    searchContainer = document.createElement("div");
    searchContainer.id = "search-container";
    wrapper.appendChild(searchContainer);

    searchWrapper = document.createElement("div");
    searchWrapper.className = "search-wrapper";
    searchContainer.appendChild(searchWrapper);

    searchInput = document.createElement("input");
    searchInput.type = "text";
    searchInput.id = "job-search-input";
    searchInput.placeholder = searchMessages.placeholder;
    searchInput.setAttribute("aria-label", searchMessages.placeholder);
    searchInput.setAttribute("role", "searchbox");
    searchInput.setAttribute("autocomplete", "off");
    searchWrapper.appendChild(searchInput);

    clearSearchBtn = document.createElement("button");
    clearSearchBtn.className = "clear-search";
    clearSearchBtn.textContent = "×";
    clearSearchBtn.setAttribute("aria-label", "Clear search");
    clearSearchBtn.setAttribute("type", "button");
    searchWrapper.appendChild(clearSearchBtn);
  }

  // Create filters + view switcher container
  const filtersViewContainer = document.createElement("div");
  filtersViewContainer.id = "filters-view-container";
  wrapper.appendChild(filtersViewContainer);

  const filtersContainer = document.createElement("div");
  filtersContainer.id = "filters-container";
  filtersViewContainer.appendChild(filtersContainer);

  // Create view switcher with accessibility attributes
  const viewSwitcher = document.createElement("div");
  viewSwitcher.id = "view-switcher";
  viewSwitcher.setAttribute("role", "group");
  viewSwitcher.setAttribute("aria-label", "View mode switcher");



  const iconSize = "24";
  const svgNamespace = "http://www.w3.org/2000/svg";

  // fix attributes
  const gridIcon = document.createElementNS(svgNamespace, "svg") as SVGSVGElement;
  gridIcon.classList.add("grid-icon");
  gridIcon.setAttribute("xmlns", svgNamespace);
  gridIcon.setAttribute("width", iconSize);
  gridIcon.setAttribute("height", iconSize);
  gridIcon.setAttribute("viewBox", "0 0 1025 1024");

  const gridPath = document.createElementNS(svgNamespace, "path");
  gridPath.setAttribute(
    "d",
    "M960.356 1024h-128q-26 0-45-19t-19-45V832q0-26 19-45t45-19h128q26 0 45 19t19 45v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-18.5t-19-45.5V448q0-27 19-45.5t45-18.5h128q26 0 45 18.5t19 45.5v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-19t-19-45V64q0-27 19-45.5t45-18.5h128q26 0 45 18.5t19 45.5v128q0 26-18.5 45t-45.5 19zm-384 768h-128q-27 0-45.5-19t-18.5-45V832q0-26 19-45t45-19h128q27 0 45.5 19t18.5 45v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-27 0-45.5-18.5t-18.5-45.5V448q0-27 19-45.5t45-18.5h128q27 0 45.5 18.5t18.5 45.5v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-27 0-45.5-19t-18.5-45V64q0-27 19-45.5t45-18.5h128q27 0 45.5 18.5t18.5 45.5v128q0 26-18.5 45t-45.5 19zm-384 768h-128q-26 0-45-19t-19-45V832q0-26 19-45t45-19h128q26 0 45 19t19 45v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-18.5t-19-45.5V448q0-27 19-45.5t45-18.5h128q26 0 45 18.5t19 45.5v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-19t-19-45V64q0-27 19-45.5t45-18.5h128q26 0 45 18.5t19 45.5v128q0 26-18.5 45t-45.5 19z"
  );
  gridIcon.appendChild(gridPath);

  const listIcon = document.createElementNS(svgNamespace, "svg") as SVGSVGElement;
  listIcon.classList.add("list-icon");
  listIcon.setAttribute("xmlns", svgNamespace);
  listIcon.setAttribute("width", iconSize);
  listIcon.setAttribute("height", iconSize);
  listIcon.setAttribute("viewBox", "0 0 1025 1024");

  const listPath = document.createElementNS(svgNamespace, "path");
  listPath.setAttribute(
    "d",
    "M960.356 1024h-512q-27 0-45.5-19t-18.5-45V832q0-26 18.5-45t45.5-19h512q26 0 45 19t19 45v128q0 27-19 45.5t-45 18.5zm0-384h-512q-27 0-45.5-18.5t-18.5-45.5V448q0-27 18.5-45.5t45.5-18.5h512q26 0 45 18.5t19 45.5v128q0 27-19 45.5t-45 18.5zm0-384h-512q-27 0-45.5-19t-18.5-45V64q0-27 18.5-45.5t45.5-18.5h512q26 0 45 18.5t19 45.5v128q0 26-19 45t-45 19zm-768 768h-128q-26 0-45-19t-19-45V832q0-26 19-45t45-19h128q26 0 45 19t19 45v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-18.5t-19-45.5V448q0-27 18.5-45.5t45.5-18.5h128q26 0 45 18.5t19 45.5v128q0 27-18.5 45.5t-45.5 18.5zm0-384h-128q-26 0-45-18.5t-19-45.5V64q0-27 18.5-45.5t45.5-18.5h128q26 0 45 18.5t19 45.5v128q0 26-19 45t-45 19z"
  );
  listIcon.appendChild(listPath);

  const gridBtn = document.createElement("button");
  gridBtn.appendChild(gridIcon);
  gridBtn.setAttribute("data-view", "grid");
  gridBtn.setAttribute("aria-label", "Switch to grid view");
  gridBtn.setAttribute("aria-pressed", "true");
  gridBtn.classList.add("active");

  const listBtn = document.createElement("button");
  listBtn.appendChild(listIcon);
  listBtn.setAttribute("data-view", "list");
  listBtn.setAttribute("aria-label", "Switch to list view");
  listBtn.setAttribute("aria-pressed", "false");

  viewSwitcher.appendChild(gridBtn);
  viewSwitcher.appendChild(listBtn);
  filtersViewContainer.appendChild(viewSwitcher);

  const adsWrapper = document.createElement("div");
  adsWrapper.id = "job-items-wrapper";
  adsWrapper.classList.add("container-fluid", "p-0");
  wrapper.appendChild(adsWrapper);

  // Create pagination container with ARIA attributes for accessibility
  const paginationContainer = document.createElement("nav");
  paginationContainer.id = "pagination-container";
  paginationContainer.setAttribute("role", "navigation");
  paginationContainer.setAttribute("aria-label", "Job listings pagination");
  wrapper.appendChild(paginationContainer);

  // Create visually hidden live region for screen reader announcements
  const liveRegion = document.createElement("div");
  liveRegion.setAttribute("role", "status");
  liveRegion.setAttribute("aria-live", "polite");
  liveRegion.setAttribute("aria-atomic", "true");
  liveRegion.className = "sr-only";
  wrapper.appendChild(liveRegion);

  state.filtersContainer = filtersContainer;
  state.adsWrapper = adsWrapper;
  state.paginationContainer = paginationContainer;
  state.liveRegion = liveRegion;

  // NOTE: keep in sync with jobLink.ts
  const createJobLink = (job: TalentechFeedItem) =>
    job.job_ad && /^https?:\/\//i.test(job.job_ad) ? job.job_ad : `${props.domain}/job/${job.job_id}`;

  const formatCaption = (location: string, department: string) => {
    const parts = [location, department].filter(Boolean);
    return parts.join(" | ");
  };

  const buildJobItemVariables = (job: TalentechFeedItem) => {
    const values: Record<string, string> = {};
    const withExtras: Record<string, string | undefined> = {
      ...job,
      button_label: props["button:label"] || "See details",
      job_url: createJobLink(job),
    };

    Object.entries(withExtras).forEach(([key, value]) => {
      values[key.toUpperCase()] = value ?? "";
    });

    return values;
  };

  const applyJobItemVariables = (
    template: string,
    variables: Record<string, string>
  ) => {
    let result = template;
    Object.entries(variables).forEach(([key, value]) => {
      const jobItemRegex = new RegExp(`\\{JOBITEM\\.${key}\\}`, "g");
      const legacyRegex = new RegExp(`\\{${key}\\}`, "g");
      result = result.replace(jobItemRegex, value);
      result = result.replace(legacyRegex, value);
    });
    return result;
  };

  const renderJobFromTemplate = (template: string, job: TalentechFeedItem) => {
    const variables = buildJobItemVariables(job);
    const html = applyJobItemVariables(template, variables).trim();
    const container = document.createElement("div");
    container.innerHTML = html;


    const firstElement = container.firstElementChild as HTMLElement | null;

    renderJobApplicationDate(firstElement?.getElementsByClassName("job-item-application-date")[0]);

    removeImagesWithoutSource(container);


    if (firstElement && container.children.length === 1) {
      if (!firstElement.classList.contains("job-item")) {
        firstElement.classList.add("job-item");
      }
      return firstElement;
    }

    const wrapperElement = document.createElement("div");
    wrapperElement.classList.add("job-item");
    wrapperElement.append(...Array.from(container.childNodes));
    return wrapperElement;
  };

  const removeImagesWithoutSource = (container: HTMLElement) => {
    container
      .querySelectorAll("img")
      .forEach((image) => {
        if (!image.getAttribute("src")) {
          image.remove();
        }
      });
  };

  const renderJobApplicationDate = (element?: Element) => {
    if (!element) {
      return;
    }
    const date = element.getAttribute("data-date");
    if (!date) {
      return;
    }
    const formattedDate = new Intl.DateTimeFormat(window.navigator.language, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(date));
    element.textContent = formattedDate;
  };

  const getFilters = (res: TalentechFeedItem[]) => {
    const availableFilters: Array<{
      id: TalentechFilterId;
      name: string;
      value: Set<string>;
    }> = [];

    if (props[CustomizableProps.FILTER_LOCATION] === "true") {
      availableFilters.push({
        id: "location",
        name: messages.filters.location.label,
        value: new Set(res.flatMap((j) => splitMulti(j.location))),
      });
    }

    if (props[CustomizableProps.FILTER_DEPARTMENT] === "true") {
      availableFilters.push({
        id: "department",
        name: messages.filters.department.label,
        value: new Set(res.flatMap((j) => splitMulti(j.department))),
      });
    }

    if (props[CustomizableProps.FILTER_CATEGORY] === "true") {
      availableFilters.push({
        id: "category",
        name: messages.filters.category.label,
        value: new Set(res.flatMap((j) => splitMulti(j.category))),
      });
    }

    if (props[CustomizableProps.FILTER_JOB_TYPE] === "true") {
      availableFilters.push({
        id: "job_type",
        name: messages.filters.job_type.label,
        value: new Set(res.flatMap((j) => splitMulti(j.job_type))),
      });
    }

    if (props[CustomizableProps.FILTER_WORKPLACE_TYPE] === "true") {
      availableFilters.push({
        id: "workplace_type",
        name: messages.filters.workplace_type.label,
        value: new Set(res.flatMap((j) => splitMulti(j.workplace_type))),
      });
    }

    return availableFilters;
  };

  const generateAds = (res: TalentechFeedItem[], viewTemplate?: string) => {
    // Use passed template or current view from window state
    const template = viewTemplate || state.currentView || "grid";

    const customTemplate =
      template === "list"
        ? props[CustomizableProps.ITEM_TEMPLATE_LIST]
        : props[CustomizableProps.ITEM_TEMPLATE_GRID];

    const ads = res.map((job) => {
      if (customTemplate && customTemplate.trim() !== "") {
        return renderJobFromTemplate(customTemplate, job);
      }
      const jobAd = document.createElement("div");
      jobAd.classList.add("job-item");

      // Caption
      const caption = document.createElement("span");
      caption.classList.add("job-item-caption");
      caption.textContent = formatCaption(job.location, job.department);

      // Heading
      const heading = document.createElement("h3");
      heading.classList.add("job-item-heading");
      heading.textContent = job.title;

      // Link/Button
      const link = document.createElement("a");
      link.classList.add("job-item-link");
      link.href = createJobLink(job);
      link.target = "_blank";
      link.textContent = props["button:label"];

      // Different structure based on template
      if (template === "list") {
        // List template: content wrapper + button on the side
        const contentWrapper = document.createElement("div");
        contentWrapper.classList.add("job-item-content");
        contentWrapper.appendChild(caption);
        contentWrapper.appendChild(heading);

        jobAd.appendChild(contentWrapper);
        jobAd.appendChild(link);
      } else {
        // Grid template: vertical stack
        jobAd.appendChild(caption);
        jobAd.appendChild(heading);
        jobAd.appendChild(link);
      }

      return jobAd;
    });

    return ads;
  };

  const getFilteredData = () => {
    let data = state.data;
    // Apply fuzzy search if search term exists
    if (state.searchTerm && state.searchTerm.trim() !== "") {
      if (state.fuse) {
        const searchResults = state.fuse.search(state.searchTerm);
        data = searchResults.map((result: any) => result.item);
      }
    }

    // Apply regular filters on top of search results
    const filtered = data.filter((job) => {
      return state.filters.every((filter) => {
        if (!filter.value) {
          return true;
        }
        // @ts-ignore
        const matches = splitMulti(job[filter.id]).includes(filter.value);
        return matches;
      });
    });

    return filtered;
  };

  const renderPage = (data: TalentechFeedItem[], page: number) => {
    const pageSize = state.pageSize;
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const pageData = data.slice(start, end);

    if (pageData.length === 0 && data.length > 0) {
      // If current page is empty but data exists, go to page 1
      state.currentPage = 1;
      renderPage(data, 1);
      return;
    }

    state.adsWrapper?.replaceChildren(...generateAds(pageData));
    updatePagination(data.length, page);
  };

  const scrollToFeedTop = () => {
    const target = document.getElementById(state.componentId) as HTMLElement
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToPage = (page: number, shouldScroll: boolean) => {
    state.currentPage = page;
    renderPage(getFilteredData(), page);
    if (shouldScroll) {
      scrollToFeedTop();
    }
  };

  const updatePagination = (totalItems: number, currentPage: number) => {
    const pageSize = state.pageSize;
    const totalPages = Math.ceil(totalItems / pageSize);

    if (totalPages <= 1) {
      paginationContainer.innerHTML = "";
      liveRegion.textContent = "";
      return;
    }

    paginationContainer.innerHTML = "";

    const paginationList = document.createElement("ul");
    paginationList.classList.add("pagination", "justify-content-start", "flex-grow-1", "mb-0");
    paginationContainer.appendChild(paginationList);
    paginationContainer.classList.add('mt-4', 'd-flex', 'align-items-center')

    const createPageItem = (
      label: string,
      onClick?: () => void,
      options?: { isDisabled?: boolean; isActive?: boolean; ariaLabel?: string }
    ) => {
      const item = document.createElement("li");
      item.className = "page-item";
      if (options?.isDisabled) {
        item.classList.add("disabled");
      }
      if (options?.isActive) {
        item.classList.add("active");
      }

      const link = document.createElement("a");
      link.classList.add("page-link", "border-0");
      link.href = "#";
      link.textContent = label;
      if (options?.ariaLabel) {
        link.setAttribute("aria-label", options.ariaLabel);
      }
      if (options?.isActive) {
        link.setAttribute("aria-current", "page");
      }
      if (options?.isDisabled) {
        link.setAttribute("aria-disabled", "true");
        link.setAttribute("tabindex", "-1");
      }
      if (onClick && !options?.isDisabled) {
        link.addEventListener("click", (event) => {
          event.preventDefault();
          onClick();
        });
      }

      item.appendChild(link);
      paginationList.appendChild(item);
    };

    const createDots = () => {
      const item = document.createElement("li");
      item.className = "page-item disabled";
      const span = document.createElement("span");
      span.className = "page-link";
      span.textContent = "...";
      span.setAttribute("aria-hidden", "true");
      item.appendChild(span);
      paginationList.appendChild(item);
    };

    // Previous button
    createPageItem("«", () => goToPage(currentPage - 1, true), {
      isDisabled: currentPage === 1,
      ariaLabel: "Previous",
    });

    // Page numbers (show current and 2 on each side)
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (startPage > 1) {
      createPageItem("1", () => goToPage(1, true), {
        ariaLabel: "Go to page 1",
      });

      if (startPage > 2) {
        createDots();
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      createPageItem(i.toString(), () => goToPage(i, true), {
        isActive: i === currentPage,
        ariaLabel:
          i === currentPage ? `Current page, page ${i}` : `Go to page ${i}`,
      });
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        createDots();
      }

      createPageItem(totalPages.toString(), () => goToPage(totalPages, true), {
        ariaLabel: `Go to page ${totalPages}`,
      });
    }

    // Next button
    createPageItem("»", () => goToPage(currentPage + 1, true), {
      isDisabled: currentPage === totalPages,
      ariaLabel: "Next",
    });

    // Page info
    const info = document.createElement("span");
    info.className = "pagination-info";
    info.setAttribute("aria-live", "polite");
    info.setAttribute("aria-atomic", "true");
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);
    const infoText = `${start}-${end} of ${totalItems}`;
    info.textContent = infoText;
    info.setAttribute("aria-label", `Showing items ${start} to ${end} of ${totalItems} total items`);
    paginationContainer.appendChild(info);

    // Announce page change to screen readers
    liveRegion.textContent = `Page ${currentPage} of ${totalPages}. Showing items ${start} to ${end} of ${totalItems}.`;
  };

  const changeFilter = (e: Event) => {
    const target = e.target as HTMLSelectElement;
    const filterId = target.name as TalentechFilterId;


    if (!target.value) {
      state.filters = state.filters.filter((filter) => filter.id !== filterId);
    } else {
      const existingFilter = state.filters.find(
        (filter) => filter.id === filterId
      );

      if (existingFilter) {
        state.filters = state.filters.map((filter) => {
            if (filter.id === filterId) {
              return {
                id: filterId,
                value: target.value,
              };
            }
            return filter;
          });
      } else {
        state.filters.push({
          id: filterId,
          value: target.value,
        });
      }
    }

    if (state.loaded) {
      if (state.data.length > 0) {
        // Reset to page 1 when filter changes
        state.currentPage = 1;
        const filteredData = getFilteredData();
        renderPage(filteredData, 1);
      }
    }
  };

  const createFiltering = (res: TalentechFeedItem[]) => {
    if (res.length > 0) {
      const categories = getFilters(res);

      const filters = categories.map((category) => {
        const wcagConnectionId = `select-${category.id}`;
        const formControl = document.createElement("div");
        formControl.classList.add("filter-form-control");
        const label = document.createElement("label");
        label.textContent = category.name;
        label.htmlFor = wcagConnectionId;
        formControl.appendChild(label);

        const select = document.createElement("select");
        select.classList.add("filter-select");
        select.name = category.id;
        select.id = wcagConnectionId;
        // @ts-ignore
        select.addEventListener("change", changeFilter);

        select.innerHTML = `<option value="">${
          messages.filters[category.id].allOption
        }</option>`;

        [...category.value].sort().forEach((value) => {
          const option = document.createElement("option");
          option.value = value;
          option.text = value;
          select.appendChild(option);
        });

        const prefilterValue = props[`prefilter:${category.id}` as CustomizableProps];
        if (prefilterValue) {
          select.value = prefilterValue;
        }

        formControl.appendChild(select);
        return formControl;
      });

      filters.forEach((filter) => filtersContainer.appendChild(filter));
    }
  };

  const applyViewTemplate = (templateId: string) => {
    // Update current view
    state.currentView = templateId;

    // Update template in component ID (to apply correct CSS)
    wrapper.setAttribute('data-template', templateId);

    // Update button states and ARIA attributes for accessibility
    const buttons = viewSwitcher.querySelectorAll('button');
    buttons.forEach((btn) => {
      if (btn.getAttribute('data-view') === templateId) {
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      } else {
        btn.classList.remove('active');
        btn.setAttribute('aria-pressed', 'false');
      }
    });

    // Re-render current page with new template
    const filteredData = getFilteredData();
    renderPage(filteredData, state.currentPage);
  };

  const render = (data: TalentechFeedItem[], allData: TalentechFeedItem[]) => {
    if (allData.length === 0) {
      // @ts-ignore
      this.style = "display: block; padding: 20px";
      adsWrapper.innerHTML = `<h2 style="text-align: center;">${props["no-data:text"]}</h2>`;
      paginationContainer.innerHTML = "";
      return;
    }

    filtersContainer.innerHTML = "";
    createFiltering(allData);

    if (data.length === 0) {
      adsWrapper.innerHTML = `<h2 style="text-align: center;">${props["no-data:text"]}</h2>`;
      paginationContainer.innerHTML = "";
    } else {
      renderPage(data, state.currentPage);
    }
  };

  const init = () => {
    const ensureFuse = () => {
      if (window.talentechLoadFuseLib) {
        return window.talentechLoadFuseLib();
      }
      return new Promise<void>((resolve) => {
        if (window.Fuse) {
          resolve();
          return;
        }
        const fuseScript = document.createElement("script");
        fuseScript.onload = () => resolve();
        fuseScript.src = "https://cdn.jsdelivr.net/npm/fuse.js@7.1.0";
        document.body.appendChild(fuseScript);
      });
    };

    // Set initial template on wrapper element
    const initialTemplate = props["template"] || "grid";
    wrapper.setAttribute('data-template', initialTemplate);

    // Set up view switcher event listeners
    gridBtn.addEventListener("click", () => applyViewTemplate("grid"));
    listBtn.addEventListener("click", () => applyViewTemplate("list"));

    // Update view switcher buttons to match initial template
    if (initialTemplate === "list") {
      gridBtn.classList.remove('active');
      gridBtn.setAttribute('aria-pressed', 'false');
      listBtn.classList.add('active');
      listBtn.setAttribute('aria-pressed', 'true');
    }

    // Set up search input event handlers (only if search is enabled)
    if (showSearch && searchInput && clearSearchBtn) {
      searchInput.addEventListener('input', (e: Event) => {
        const target = e.target as HTMLInputElement;
        state.searchTerm = target.value;

        // Show/hide clear button
        if (target.value && clearSearchBtn) {
          clearSearchBtn.classList.add('visible');
        } else if (clearSearchBtn) {
          clearSearchBtn.classList.remove('visible');
        }

        // Reset to page 1 and filter data
        if (state.loaded) {
          state.currentPage = 1;
          const filteredData = getFilteredData();
          renderPage(filteredData, 1);
        }
      });

      clearSearchBtn.addEventListener('click', () => {
        if (searchInput && clearSearchBtn) {
          searchInput.value = '';
          state.searchTerm = '';
          clearSearchBtn.classList.remove('visible');
          searchInput.focus();

          // Reset to page 1 and filter data
          if (state.loaded) {
            state.currentPage = 1;
            const filteredData = getFilteredData();
            renderPage(filteredData, 1);
          }
        }
      });
    }

    if (state.loaded) {
      state.messages = messages;
      broadcastMessagesUpdated();
      // Re-render with existing data and filters
      const filteredData = getFilteredData();
      render(filteredData, state.data);
      return;
    }

    ensureFuse().then(() => {
      fetch(`${props.source}`).then((res) => {
        res.json().then((res: TalentechFeedItem[]) => {
          const initialFilters: Array<{ id: TalentechFilterId; value: string }> = [];

          const prefilterLocation = props["prefilter:location"];
          if (prefilterLocation) {
            initialFilters.push({ id: "location", value: prefilterLocation });
          }

          const prefilterDepartment = props["prefilter:department"];
          if (prefilterDepartment) {
            initialFilters.push({ id: "department", value: prefilterDepartment });
          }

          const prefilterCategory = props["prefilter:category"];
          if (prefilterCategory) {
            initialFilters.push({ id: "category", value: prefilterCategory });
          }

          const prefilterJobType = props["prefilter:job_type"];
          if (prefilterJobType) {
            initialFilters.push({ id: "job_type", value: prefilterJobType });
          }

          const prefilterWorkplaceType = props["prefilter:workplace_type"];
          if (prefilterWorkplaceType) {
            initialFilters.push({
              id: "workplace_type",
              value: prefilterWorkplaceType,
            });
          }

          const pageSize = parseInt(props[CustomizableProps.PAGE_SIZE] || "9", 10);

          // Initialize Fuse.js for fuzzy search on job titles
          const fuse = new window.Fuse(res, {
            keys: ['title'],
            threshold: 0.3,
            includeScore: true,
            minMatchCharLength: 2,
            ignoreLocation: true,
          });

          state.loaded = true;
          state.data = res;
          state.filters = initialFilters;
          state.currentPage = 1;
          state.currentView = props["template"] || "grid";
          state.pageSize = pageSize;
          state.fuse = fuse;
          state.searchTerm = '';

          broadcastMessagesUpdated();

          const filteredData = getFilteredData();
          render(filteredData, res);
        });
      });
    });
  };
  init();
};
